import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { productLabel } from "@/planner/lib/format";
import { ProductPicker } from "@/planner/pages/Receipts";
import { WasteForm } from "@/planner/components/WasteDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, CameraOff, Check, Minus, Plus, ScanBarcode, Undo2 } from "lucide-react";

// Count backstock with the phone camera: scan a barcode, type how many you have, save, scan the next.
// Barcodes are looked up with /inventory/barcode/{code} (UPC-A, EAN-13 and Sandstar's padded codes all
// match); unknown ones can be tied to a product so the next scan finds it. Counts are saved with
// /receipts/on-hand/{id}; in waste mode the scanned item is logged as waste (/waste) instead. The scanner library loads only when the camera starts.

const SCAN_FORMATS = ["EAN_13", "EAN_8", "UPC_A", "UPC_E", "CODE_128"];
const beep = () => {
  try { navigator.vibrate?.(60); } catch { /* not supported */ }
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = 1300; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.08); o.onended = () => ctx.close();
  } catch { /* audio blocked */ }
};

const Scanner = ({ active, onCode }) => {
  const video = useRef(null);
  const controls = useRef(null);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);

  const stop = useCallback(() => { controls.current?.stop(); controls.current = null; setRunning(false); }, []);
  const start = useCallback(async () => {
    setError("");
    try {
      const [{ BrowserMultiFormatOneDReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([import("@zxing/browser"), import("@zxing/library")]);
      const hints = new Map([[DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS.map((f) => BarcodeFormat[f])]]);
      const reader = new BrowserMultiFormatOneDReader(hints, { delayBetweenScanAttempts: 120 });
      controls.current = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } } },
        video.current, (result) => { if (result) onCode(result.getText()); });
      setRunning(true);
    } catch (e) {
      setRunning(false);
      setError(e?.name === "NotAllowedError"
        ? "Camera access was blocked. Allow the camera for this site in your browser settings, or type the barcode below."
        : "Couldn't start the camera. Type the barcode or search below instead.");
    }
  }, [onCode]);
  useEffect(() => stop, [stop]);

  return (
    <div className="relative overflow-hidden rounded-xl border bg-black" data-testid="scanner">
      <video ref={video} className={`aspect-[4/3] w-full object-cover sm:aspect-video ${running ? "" : "opacity-0"}`} muted playsInline />
      {running && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className={`h-1/3 w-4/5 rounded-lg border-2 ${active ? "border-sky-400" : "border-white/30"}`} />
        </div>
      )}
      {!running && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center text-sm text-white">
          <ScanBarcode className="h-10 w-10 opacity-70" />
          {error ? <p className="max-w-sm text-amber-200">{error}</p> : <p className="max-w-sm opacity-80">Point the camera at a barcode. It reads UPC and EAN codes on cans, bottles and bags.</p>}
          <Button type="button" onClick={start} data-testid="scanner-start"><Camera className="mr-1.5 h-4 w-4" />Start camera</Button>
        </div>
      )}
      {running && (
        <Button type="button" size="sm" variant="secondary" className="absolute right-2 top-2" onClick={stop}><CameraOff className="mr-1.5 h-4 w-4" />Stop</Button>
      )}
    </div>
  );
};

export default function CountStock() {
  const [products, setProducts] = useState([]);
  const [code, setCode] = useState("");            // the barcode being handled
  const [unknown, setUnknown] = useState(false);
  const [prod, setProd] = useState(null);
  const [count, setCount] = useState("");
  const [mode, setMode] = useState("set");         // "set" the count, "add" a box to it, or log "waste"
  const [saving, setSaving] = useState(false);
  const [typed, setTyped] = useState("");
  const [log, setLog] = useState([]);              // counted this session
  const busy = useRef(false);
  const last = useRef({ code: "", at: 0 });
  const countInput = useRef(null);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (prod) setTimeout(() => countInput.current?.focus(), 50); }, [prod]);

  const open = (p) => { setProd(p); setUnknown(false); setCount(mode === "set" ? String(p.on_hand ?? 0) : ""); };

  const lookup = useCallback(async (raw) => {
    const c = String(raw).replace(/\D/g, "");
    if (!c) return;
    busy.current = true;
    setCode(c); setProd(null); setUnknown(false);
    try {
      const { data } = await api.get(`/inventory/barcode/${c}`);
      open(data);
    } catch (e) {
      if (e?.response?.status === 404) setUnknown(true); else toast.error("Couldn't look that up.");
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // The camera keeps reading; ignore codes while one is open, and the code just saved until it has been
  // out of view for a moment (so it isn't reopened while the item is still in front of the camera).
  const onScan = useCallback((c) => {
    const now = Date.now();
    if (c === last.current.code && now - last.current.at < 1500) { last.current.at = now; return; }
    if (busy.current) return;
    busy.current = true;
    last.current = { code: c, at: now };
    beep();
    lookup(c);
  }, [lookup]);

  const done = () => { setProd(null); setUnknown(false); setCode(""); busy.current = false; };

  const save = async (e) => {
    e?.preventDefault();
    const n = Number(count);
    if (count === "" || !(n >= 0)) { toast.error("Enter how many you have."); return; }
    const before = prod.on_hand || 0;
    const next = mode === "add" ? before + Math.round(n) : Math.round(n);
    setSaving(true);
    try {
      await api.put(`/receipts/on-hand/${prod.id}`, { on_hand: next });
      setProducts((ps) => ps.map((p) => (p.id === prod.id ? { ...p, on_hand: next } : p)));
      setLog((l) => [{ id: prod.id, name: productLabel(prod), size: prod.size, before, after: next, at: Date.now() }, ...l]);
      toast.success(`${productLabel(prod)}: ${next} on hand`);
      last.current = { code, at: Date.now() };
      done();
    } catch {
      toast.error("Couldn't save the count.");
    } finally { setSaving(false); }
  };

  const wasted = (w) => {
    setProducts((ps) => ps.map((p) => (p.id === prod.id ? { ...p, on_hand: w.on_hand } : p)));
    setLog((l) => [{ id: prod.id, name: productLabel(prod), size: prod.size, before: prod.on_hand || 0, after: w.on_hand, waste: w, at: Date.now() }, ...l]);
    last.current = { code, at: Date.now() };
    done();
  };

  const undo = async (entry) => {
    try {
      if (entry.waste) await api.delete(`/waste/${entry.waste.id}`);
      else await api.put(`/receipts/on-hand/${entry.id}`, { on_hand: entry.before });
      setProducts((ps) => ps.map((p) => (p.id === entry.id ? { ...p, on_hand: entry.before } : p)));
      setLog((l) => l.filter((x) => x !== entry));
      toast.success(`${entry.name} back to ${entry.before}`);
    } catch { toast.error("Couldn't undo."); }
  };

  const linkTo = async (p) => {
    try {
      const { data } = await api.post(`/inventory/barcode/${code}/link`, { product_id: p.id });
      setProducts((ps) => ps.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Barcode saved on ${productLabel(data)}`);
      open(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the barcode."); }
  };

  const step = (d) => setCount((c) => String(Math.max(0, (Number(c) || 0) + d)));

  return (
    <div className="mx-auto max-w-2xl" data-testid="count-page">
      <div className="mb-4">
        <h1 className="font-heading text-2xl font-bold">Count Stock</h1>
        <p className="text-sm text-muted-foreground">Scan an item, enter how many you have, save, and scan the next. Counts update your <Link to="/admin/planner/inventory" className="underline">backstock</Link>. Broke or expired? Switch to Log waste and scan it.</p>
      </div>

      <div className="mb-3 flex gap-1 rounded-lg bg-muted p-1 text-sm" role="radiogroup" aria-label="How to count">
        {[["set", "Set the count"], ["add", "Add a box"], ["waste", "Log waste"]].map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
            className={`flex-1 rounded-md px-3 py-1.5 ${mode === k ? "bg-background font-semibold shadow" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      <Scanner active={!prod && !unknown} onCode={onScan} />

      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (/^\d{6,}$/.test(typed.trim())) { lookup(typed.trim()); setTyped(""); } }}>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} inputMode="numeric" placeholder="Or type the barcode number" data-testid="count-typed" />
        <Button type="submit" variant="outline">Find</Button>
      </form>
      {!prod && !unknown && (
        <div className="mt-2"><ProductPicker products={products} onPick={(p) => { setCode(""); busy.current = true; open(p); }} /></div>
      )}

      {unknown && (
        <Card className="mt-4 space-y-3 p-4" data-testid="count-unknown">
          <p className="text-sm">Barcode <span className="font-mono font-semibold">{code}</span> isn't on any of your products yet. Which product is it? It'll be found by this barcode from now on.</p>
          <ProductPicker products={products} onPick={linkTo} />
          <Button type="button" variant="ghost" size="sm" onClick={done}>Skip it</Button>
        </Card>
      )}

      {prod && mode === "waste" && (
        <Card className="mt-4 p-4" data-testid="count-waste">
          <p className="font-semibold leading-tight">{productLabel(prod)}</p>
          <p className="mb-3 text-xs text-muted-foreground">{[prod.size, code && `#${code}`].filter(Boolean).join(" · ")}</p>
          <WasteForm product={prod} onCancel={done} onSaved={wasted} />
        </Card>
      )}

      {prod && mode !== "waste" && (
        <Card className="mt-4 p-4" data-testid="count-product">
          <form onSubmit={save}>
            <div className="flex items-start gap-3">
              {fileUrl(prod.image_path) ? <img src={fileUrl(prod.image_path)} alt="" className="h-16 w-16 shrink-0 rounded bg-white object-contain" />
                : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-muted"><ScanBarcode className="h-6 w-6 text-muted-foreground" /></div>}
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-tight">{productLabel(prod)}</p>
                <p className="text-xs text-muted-foreground">{[prod.size, code && `#${code}`].filter(Boolean).join(" · ")}</p>
                <p className="mt-1 text-sm">On hand now: <b>{prod.on_hand ?? 0}</b>
                  {prod.counted_at && <span className="text-xs text-muted-foreground"> · counted {new Date(prod.counted_at).toLocaleDateString()}</span>}</p>
              </div>
            </div>
            <label className="mt-4 block text-sm font-medium" htmlFor="count-input">{mode === "add" ? "How many in this box?" : "How many do you have?"}</label>
            <div className="mt-1 flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => step(-1)} aria-label="One less"><Minus className="h-5 w-5" /></Button>
              <Input id="count-input" ref={countInput} type="number" inputMode="numeric" min="0" value={count} onChange={(e) => setCount(e.target.value)}
                onFocus={(e) => e.target.select()} className="h-12 text-center text-2xl font-bold" data-testid="count-input" />
              <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => step(1)} aria-label="One more"><Plus className="h-5 w-5" /></Button>
            </div>
            {mode === "add" && count !== "" && <p className="mt-1 text-xs text-muted-foreground">New total {(prod.on_hand || 0) + (Math.round(Number(count)) || 0)}</p>}
            <div className="mt-4 flex gap-2">
              <Button type="button" variant="ghost" onClick={done}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="count-save"><Check className="mr-1.5 h-4 w-4" />{saving ? "Saving…" : "Save and scan next"}</Button>
            </div>
          </form>
        </Card>
      )}

      {log.length > 0 && (
        <div className="mt-6" data-testid="count-log">
          <h2 className="mb-2 text-sm font-semibold">Done just now ({log.length})</h2>
          <Card className="divide-y">
            {log.map((x) => (
              <div key={`${x.id}-${x.at}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{x.name}{x.size && <span className="text-muted-foreground"> · {x.size}</span>}</span>
                {x.waste && <span className="rounded bg-red-100 px-1.5 text-xs text-red-800">waste {x.waste.units}</span>}
                <span className="font-mono text-xs text-muted-foreground">{x.before} → <b className="text-foreground">{x.after}</b></span>
                <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => undo(x)} aria-label={`Undo ${x.name}`} title="Undo"><Undo2 className="h-4 w-4" /></button>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
